import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, AlertTitle, AlertDescription } from "@shadcn/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { UI } from "@forms/form-render/ui";
import { Step } from "./Step";
import { UserJourneyProvider, useUserJourneyContext } from "@/context/UserJourneyContext";
import { getEnquiryCookie } from "@/data/enquiries/cookie";
import {
  getFirstStepDisplayIdFromPipeline,
  isFirstStepInPipeline,
} from "@/data/journeyPipeline";
import { Schema } from "@schema-types/types";
import { isJourneyTerminalStep } from "@schema-types/types/journeyTerminalStep";
import { CallMeBackButton } from "@/components/CallMeBackButton";

type JourneyContentProps = {
  journeyDisplayId: string;
  stepId?: string;
};

const getDisplayIdForStep = (journey: Schema.JourneySchema, stepId: string): string | null => {
  const step = journey.steps.find((s) => s.id === stepId || s.displayId === stepId);
  if (!step) return null;
  return step.displayId ?? step.id ?? null;
};

const isTerminalStepForSession = (journey: Schema.JourneySchema, stepIdOrDisplayId: string): boolean => {
  const step = journey.steps.find((s) => s.id === stepIdOrDisplayId || s.displayId === stepIdOrDisplayId);
  if (!step) return false;
  return isJourneyTerminalStep(step);
};

const JourneyContent = ({ journeyDisplayId, stepId }: JourneyContentProps) => {
  const navigate = useNavigate();
  const {
    journey,
    journeyLoading,
    journeyError,
    journeyNotFound,
    journeyVersion,
    enquiry,
    enquiryLoading,
    enquiryError,
    showNotification,
  } = useUserJourneyContext();

  useEffect(() => {
    if (sessionStorage.getItem("instructd_session_lost")) {
      sessionStorage.removeItem("instructd_session_lost");
      showNotification("Your session was lost. You must start from the beginning.", "info");
    }
  }, [showNotification]);

  useEffect(() => {
    if (!journey || journeyLoading || enquiryLoading) {
      return;
    }

    const firstStepDisplayId = getFirstStepDisplayIdFromPipeline(journey);
    const targetStepFromUrl = stepId ? getDisplayIdForStep(journey, stepId) : null;

    const enquiryCookie = getEnquiryCookie();
    const hasMatchingEnquiryCookie =
      enquiryCookie &&
      journey.id &&
      journeyVersion &&
      enquiryCookie.journeyId === journey.id &&
      enquiryCookie.version === journeyVersion;

    const isMidJourneyWithNoSession =
      targetStepFromUrl &&
      firstStepDisplayId &&
      !isFirstStepInPipeline(journey, stepId) &&
      !isTerminalStepForSession(journey, stepId) &&
      !hasMatchingEnquiryCookie;

    if (isMidJourneyWithNoSession) {
      sessionStorage.setItem("instructd_session_lost", "true");
      const newUrl = `${window.location.pathname}${window.location.search || ""}#/${firstStepDisplayId}`;
      window.location.replace(newUrl);
      return;
    }

    if (targetStepFromUrl) {
      return;
    }

    const enquiryStepDisplayId = enquiry?.currentStepId
      ? getDisplayIdForStep(journey, enquiry.currentStepId)
      : null;

    const fallbackStepDisplayId = enquiryStepDisplayId ?? firstStepDisplayId;

    if (fallbackStepDisplayId) {
      navigate(`/${fallbackStepDisplayId}`, { replace: true });
    }
  }, [journey, journeyLoading, journeyVersion, enquiry, enquiryLoading, stepId, navigate, showNotification]);

  if (!journeyDisplayId) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invalid Request</AlertTitle>
          <AlertDescription>No journey ID provided</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (journeyNotFound) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Journey Not Found</AlertTitle>
          <AlertDescription>No journey deployed to this environment</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (journeyError) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Journey Error</AlertTitle>
          <AlertDescription>{journeyError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (enquiryError) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{enquiryError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Only show skeleton when journey is loading, not when session is loading
  // This keeps the form visible and just disables inputs during session creation
  if (journeyLoading || !journey) {
    return <UI.FormSkeleton />;
  }

  const journeyHasError = journey?.hasError || !!journey?.errorMessage;

  if (journeyHasError) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Journey Error</AlertTitle>
          <AlertDescription>
            {journey.errorMessage || "This journey cannot be loaded due to an error. Please contact support."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const targetStep = stepId ? getDisplayIdForStep(journey, stepId) : null;

  if (!targetStep) {
    return <UI.FormSkeleton />;
  }

  return (
    <>
      <CallMeBackButton />
      <Step journeySchema={journey} currentStepId={targetStep} />
    </>
  );
};

export const Journey = () => {
  const { stepId } = useParams<{ stepId: string }>();
  const pathParts = window.location.pathname.split("/");
  const journeyDisplayId = pathParts[1];

  return (
    <UserJourneyProvider journeyDisplayId={journeyDisplayId}>
      <JourneyContent journeyDisplayId={journeyDisplayId} stepId={stepId} />
    </UserJourneyProvider>
  );
};